import { Col, Form, FormInstance, Row, Select, Spin } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  getDoctors,
  selectAllDoctors,
} from '../../../state/doctor/doctorReducer'
import { getNurses, selectAllNurses } from '../../../state/nurse/nurseReducer'
import { AlertConfiguration } from '../../../state/patientAlertConfiguration/types'

const notAssignedOption = { value: '', label: '-- Not Assigned --' }

const AntdForm = styled(Form)`
  & span.ant-select-selection-placeholder {
    display: flex;
    justify-content: center;
  }
`

const SpinContainer = styled.div`
  display: flex;
  justify-content: center;
`

type Props = {
  form: FormInstance<any>
  disabledState: boolean
  onFinish?: (values?: any) => void
  onFinishFailed?: (values?: any) => void
  size?: 'middle' | 'small' | 'large'
  defaultValue?: AlertConfiguration
}

function AlertConfigurationForm({
  disabledState,
  form,
  onFinish,
  onFinishFailed,
  size,
  defaultValue,
}: Props) {
  const dispatch = useDispatch()
  const nurses = useSelector(selectAllNurses)
  const doctors = useSelector(selectAllDoctors)
  const [alertDoctorKey, setAlertDoctorKey] = useState<string | undefined>()
  const [alertNurseKey, setAlertNurseKey] = useState<string | undefined>()
  const [alertNurse2Key, setAlertNurse2Key] = useState<string | undefined>()

  const doctorSelectDetails = useMemo(
    () =>
      doctors.map((doctor) => {
        const phoneNumbers = [
          doctor.phonePrimary,
          doctor.phoneSecondary,
        ].filter((value) => value)

        return {
          value: doctor.doctorKey,
          label: `${doctor.title} ${doctor.firstName} ${doctor.lastName}`,
          phones: phoneNumbers,
        }
      }),
    [doctors]
  )

  const nurseSelectDetails = useMemo(
    () =>
      nurses.map((nurse) => {
        const phoneNumbers = [nurse.phonePrimary, nurse.phoneSecondary].filter(
          (value) => value
        )
        return {
          value: nurse.nurseKey,
          label: `${nurse.title} ${nurse.firstName} ${nurse.lastName}`,
          phones: phoneNumbers,
        }
      }),
    [nurses]
  )

  const doctorNameOptions = useMemo(
    () =>
      [notAssignedOption, ...doctorSelectDetails].map((doctor) => ({
        value: doctor.value,
        label: doctor.label,
      })),
    [doctorSelectDetails]
  )

  const nurseNameOptions = useMemo(
    () =>
      [notAssignedOption, ...nurseSelectDetails].map((nurse) => ({
        value: nurse.value,
        label: nurse.label,
      })),
    [nurseSelectDetails]
  )

  const alertNurse1PhoneNumberOptions = useMemo(() => {
    const selectedNurse = nurseSelectDetails.find(
      (nurse) => nurse.value === alertNurseKey
    )

    if (!selectedNurse) return []

    return selectedNurse.phones
      .map((phone) => ({ value: phone, label: phone }))
      .filter((data) => data.value.trim())
  }, [alertNurseKey, nurseSelectDetails])

  const alertNurse2PhoneNumberOptions = useMemo(() => {
    const selectedNurse2 = nurseSelectDetails.find(
      (nurse) => nurse.value === alertNurse2Key
    )

    if (!selectedNurse2) return []

    return selectedNurse2.phones
      .map((phone) => ({ value: phone, label: phone }))
      .filter((data) => data.value.trim())
  }, [alertNurse2Key, nurseSelectDetails])

  const alertDoctorPhoneNumberOptions = useMemo(() => {
    const selectedDoctor = doctorSelectDetails.find(
      (doctor) => doctor.value === alertDoctorKey
    )

    if (!selectedDoctor) return []

    return selectedDoctor.phones
      .map((phone) => ({ value: phone, label: phone }))
      .filter((data) => data.value.trim())
  }, [alertDoctorKey, doctorSelectDetails])

  function onChangeNurse1(value: string) {
    setAlertNurseKey(value)
    form.setFieldValue('firstNumber', '')
  }

  function onChangeNurse2(value: string) {
    setAlertNurse2Key(value)
    form.setFieldValue('secondNumber', '')
  }

  function onChangeDoctor(value: string) {
    setAlertDoctorKey(value)
    form.setFieldValue('doctorNumber', '')
  }

  useEffect(() => {
    if (defaultValue) {
      setAlertDoctorKey(defaultValue.firstAlertDoctorKey)
      setAlertNurseKey(defaultValue.firstAlertNurseKey)
      setAlertNurse2Key(defaultValue.escalationAlertNurseKey)
    }
  }, [defaultValue])

  useEffect(() => {
    dispatch(getNurses())
    dispatch(getDoctors())
  }, [dispatch])

  if (doctorSelectDetails.length === 0 || nurseSelectDetails.length === 0) {
    return (
      <SpinContainer>
        <Spin size='large' />
      </SpinContainer>
    )
  }

  return (
    <AntdForm
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      size={size}
      requiredMark={!disabledState}
      layout='horizontal'
      disabled={disabledState}
      form={form}
      initialValues={defaultValue}
      className='disabledViewForm alertConfigureForm'
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='firstAlertNurseKey' label='Level l Alert'>
            <Select
              allowClear={true}
              placeholder='-- Not Assigned --'
              value={alertNurseKey}
              onChange={onChangeNurse1}
              options={nurseNameOptions}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='firstNumber' label='Phone Number'>
            <Select
              options={alertNurse1PhoneNumberOptions}
              onChange={() => {
                form.setFields([
                  {
                    name: 'firstNumber',
                    errors: [],
                  },
                ])
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='escalationAlertNurseKey' label='Level ll Alert'>
            <Select
              allowClear={true}
              placeholder='-- Not Assigned --'
              value={alertNurse2Key}
              onChange={onChangeNurse2}
              options={nurseNameOptions}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='secondNumber' label='Phone Number'>
            <Select
              options={alertNurse2PhoneNumberOptions}
              onChange={() => {
                form.setFields([
                  {
                    name: 'secondNumber',
                    errors: [],
                  },
                ])
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='firstAlertDoctorKey' label='Level lll Alert'>
            <Select
              allowClear={true}
              placeholder='-- Not Assigned --'
              value={alertDoctorKey}
              onChange={onChangeDoctor}
              options={doctorNameOptions}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='doctorNumber' label='Phone Number'>
            <Select
              options={alertDoctorPhoneNumberOptions}
              onChange={() => {
                form.setFields([
                  {
                    name: 'doctorNumber',
                    errors: [],
                  },
                ])
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </AntdForm>
  )
}

export default AlertConfigurationForm
